import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { timeAgo } from "@/lib/format";
import { Building2, Loader2, Phone, Mail } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "converted", "lost"];
const STATUS_STYLE = {
  new: "bg-[#F83A84]/10 text-[#F83A84]",
  contacted: "bg-[#2563EB]/10 text-[#2563EB]",
  qualified: "bg-[#7C3AED]/10 text-[#7C3AED]",
  converted: "bg-[#0B7C5A]/10 text-[#0B7C5A]",
  lost: "bg-[#6B7189]/10 text-[#6B7189]",
};

export default function Leads() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/super-admin/demo-requests"); setRows(data.requests || []); }
    catch (e) { toast.error(apiError(e)); setRows([]); }
  };
  useEffect(() => { if (user?.is_super_admin) load(); }, [user]);

  const setStatus = async (rid, status) => {
    setSaving(rid);
    try {
      await api.patch(`/super-admin/demo-requests/${rid}`, { status });
      setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, status } : r)));
      toast.success("Lead updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(""); }
  };

  if (!user?.is_super_admin) {
    return <div className="p-8 text-center text-[#6B7189]" data-testid="leads-forbidden">Leads are visible to TradeDesk360 sales (Clotus admin) only.</div>;
  }

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: (rows || []).filter((r) => (r.status || "new") === s).length }), {});

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="leads-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-[#101D51] flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
        <div>
          <h1 className="text-2xl font-bold text-[#101D51]">Demo & Trial Leads</h1>
          <p className="text-sm text-[#6B7189]">Website enquiries from the landing page. Track and follow up.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 my-4" data-testid="leads-summary">
        <span className="px-3 py-1 rounded-full bg-[#101D51] text-white text-xs font-semibold">Total {rows?.length || 0}</span>
        {STATUSES.map((s) => (
          <span key={s} className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[s]}`}>{s} {counts[s] || 0}</span>
        ))}
      </div>

      {rows === null ? (
        <div className="flex items-center justify-center py-20 text-[#6B7189]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E6E8F0] rounded-2xl p-12 text-center text-[#6B7189]" data-testid="leads-empty">No leads yet. New demo requests from the website will appear here.</div>
      ) : (
        <div className="bg-white border border-[#E6E8F0] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-[#F8F9FC] text-left text-[11px] uppercase tracking-wide text-[#6B7189]">
                  <th className="px-4 py-3">Lead</th><th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Industry</th><th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Received</th><th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#F0F1F5] align-top" data-testid={`lead-row-${r.id}`}>
                    <td className="px-4 py-3"><div className="font-semibold text-[#101D51]">{r.name || "—"}</div><div className="text-[#6B7189] text-xs">{r.business || "—"}</div></td>
                    <td className="px-4 py-3 text-xs">
                      {r.phone && <a href={`tel:${r.phone}`} className="flex items-center gap-1 text-[#101D51] hover:underline" data-testid={`lead-phone-${r.id}`}><Phone className="w-3 h-3" /> {r.phone}</a>}
                      {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-1 text-[#1D4ED8] hover:underline mt-0.5" data-testid={`lead-email-${r.id}`}><Mail className="w-3 h-3" /> {r.email}</a>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#3B4468]">{r.industry || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#3B4468] max-w-[16rem]"><div className="line-clamp-3">{r.message || "—"}</div></td>
                    <td className="px-4 py-3 text-xs text-[#6B7189] whitespace-nowrap">{timeAgo(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <select value={r.status || "new"} disabled={saving === r.id} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`lead-status-${r.id}`}
                        className={`text-xs font-semibold rounded-lg px-2 py-1.5 border border-[#E6E8F0] capitalize focus:outline-none focus:ring-2 focus:ring-[#101D51]/20 ${STATUS_STYLE[r.status || "new"]}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
